import { useEffect, useState, useMemo } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Plus, Save, Trash2, Search, X } from "lucide-react";
import { toast } from "react-toastify";
import amsProjectAuditService, { CreateAmsProjectAudit } from "../../../services/amsProjectAuditService";
import auditTypeService, { AuditType } from "../../../services/auditTypeService";
import { userService } from "../../../services/userService";
import { User } from "../../../types/user";
import Label from "../../../components/form/Label";
import Select from "../../../shared/components/form/Select";
import Input from "../../../components/form/input/InputField";

interface AmsProjectAuditFormProps {
  amsProjectId: string;
  projectAuditors?: string[]; // Array of auditor user IDs from project's assignedResources.auditors
  projectScope?: number; // Project scope to auto-fill in audit form (default per audit row)
  projectStartDate?: string; // Project start date (DD/MM/YYYY or YYYY-MM-DD) – default for audit start date
  projectEndDate?: string; // Project end date – validation upper bound for audit start date
  onSuccess?: () => void;
}

/** Convert DD/MM/YYYY or YYYY-MM-DD to YYYY-MM-DD for input[type=date] */
function toInputDate(dateStr: string | undefined): string {
  if (!dateStr || !dateStr.trim()) return '';
  const s = dateStr.trim();
  const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return `${y}-${(m ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

/** Parse to Date for comparison (DD/MM/YYYY or YYYY-MM-DD) */
function parseToDate(dateStr: string | undefined): Date | null {
  if (!dateStr || !dateStr.trim()) return null;
  const s = dateStr.trim();
  const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    const date = new Date(parseInt(y ?? '', 10), parseInt(m ?? '', 10) - 1, parseInt(d ?? '', 10));
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(s.slice(0, 10));
  return isNaN(date.getTime()) ? null : date;
}

// Audit Form Schema (projectStartDate/projectEndDate passed in for context-based validation)
const buildAuditFormSchema = (projectStartDate?: string, projectEndDate?: string) =>
  yup.object({
    audits: yup
      .array()
      .of(
        yup.object({
          auditTypeId: yup
            .string()
            .required('Audit type is required'),
          auditFrequency: yup
            .string()
            .oneOf(['yearly', 'half_yearly', 'quarterly'] as const)
            .required('Audit frequency is required'),
          auditors: yup
            .array()
            .of(yup.string().required())
            .min(1, 'At least one auditor is required')
            .required('At least one auditor is required'),
          scope: yup
            .number()
            .required('Scope is required')
            .min(0, 'Scope must be at least 0')
            .max(100, 'Scope must not be greater than 100 percent')
            .typeError('Scope must be a number'),
          auditStartDate: yup
            .string()
            .optional()
            .test('in-range', 'Audit start date must be between project start and end date', function (value) {
              if (!value || !value.trim()) return true;
              const start = parseToDate(projectStartDate);
              const end = parseToDate(projectEndDate);
              const audit = parseToDate(value);
              if (!audit) return false;
              if (start && audit < start) return false;
              if (end && audit > end) return false;
              return true;
            }),
        }).required()
      )
      .min(1, 'At least one audit is required')
      .required('At least one audit is required'),
  });

type AuditFormValues = yup.InferType<ReturnType<typeof buildAuditFormSchema>>;

export default function AmsProjectAuditForm({
  amsProjectId,
  projectAuditors = [],
  projectScope,
  projectStartDate,
  projectEndDate,
  onSuccess,
}: AmsProjectAuditFormProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submittingAudits, setSubmittingAudits] = useState(false);
  const [auditorSearchTerms, setAuditorSearchTerms] = useState<Record<number, string>>({});
  const [activeAuditTypes, setActiveAuditTypes] = useState<AuditType[]>([]);
  const [loadingAuditTypes, setLoadingAuditTypes] = useState(false);

  const defaultAuditStart = toInputDate(projectStartDate);

  const {
    control: auditControl,
    handleSubmit: handleAuditSubmit,
    reset: resetAuditForm,
    watch,
    formState: { errors: auditErrors },
  } = useForm<AuditFormValues>({
    resolver: yupResolver(buildAuditFormSchema(projectStartDate, projectEndDate)) as any,
    defaultValues: {
      audits: [
        {
          auditTypeId: undefined as any,
          auditFrequency: undefined as any,
          auditors: [],
          scope: projectScope ?? undefined,
          auditStartDate: defaultAuditStart || undefined,
        },
      ],
    },
  });

  const { fields: auditFields, append: appendAudit, remove: removeAudit } = useFieldArray({
    control: auditControl,
    name: 'audits',
  });

  // Audit type options — fetched live from the AuditType master (replaces the old
  // hardcoded 4-value enum).
  const auditTypeOptions = activeAuditTypes.map((t) => ({ value: t._id, label: t.name }));

  // Audit frequency options
  const auditFrequencyOptions = [
    { value: 'yearly', label: 'Yearly' },
    { value: 'half_yearly', label: 'Half Yearly' },
    { value: 'quarterly', label: 'Quarterly' },
  ];

  // Filtered auditors for each audit field - only show project auditors
  const getFilteredAuditors = (index: number) => {
    // Filter users to only include project auditors
    const projectAuditorUsers = projectAuditors.length > 0
      ? users.filter(user => {
          const userId = String(user._id || user.id || '');
          return projectAuditors.some(auditorId => String(auditorId) === userId);
        })
      : users; // Fallback to all users if no project auditors specified
    
    const searchTerm = auditorSearchTerms[index] || '';
    if (!searchTerm) return projectAuditorUsers;
    return projectAuditorUsers.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Allow same audit type with different frequency – show all audit types for every row
  const getAvailableAuditTypes = () => auditTypeOptions;

  // Fetch users for auditors dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch active audit types for the audit type dropdown
  useEffect(() => {
    const fetchAuditTypes = async () => {
      setLoadingAuditTypes(true);
      try {
        const types = await auditTypeService.getAllActiveAuditTypes();
        setActiveAuditTypes(types);
      } catch (error: any) {
        console.error('Failed to fetch audit types:', error);
        toast.error('Failed to load audit types');
      } finally {
        setLoadingAuditTypes(false);
      }
    };
    fetchAuditTypes();
  }, []);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await userService.getUsers({
        status: 'active',
        limit: 1000,
        populate: 'role',
      });
      setUsers(response.users);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle audit form submission
  const onSubmitAudits = async (data: AuditFormValues) => {
    setSubmittingAudits(true);
    try {
      const result = await amsProjectAuditService.createBulkAudits({
        amsProjectId,
        audits: data.audits as CreateAmsProjectAudit[],
      });

      if (result.failed.length > 0) {
        // Show errors for failed audits
        result.failed.forEach((failure) => {
          const auditTypeName = activeAuditTypes.find((t) => t._id === failure.audit.auditTypeId)?.name
            || failure.audit.auditTypeId;
          toast.error(`Failed to create audit "${auditTypeName}": ${failure.error}`);
        });
      }

      if (result.created.length > 0) {
        toast.success(`Successfully created ${result.created.length} audit(s)`);
        resetAuditForm();
        if (onSuccess) {
          onSuccess();
        }
      } else if (result.failed.length > 0) {
        toast.error('Failed to create any audits');
      }
    } catch (error: any) {
      console.error('Failed to create audits:', error);
      toast.error(error?.response?.data?.message || 'Failed to create audits');
    } finally {
      setSubmittingAudits(false);
    }
  };

  if (!amsProjectId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleAuditSubmit(onSubmitAudits)} className="space-y-6">
          {auditFields.map((field, index) => {
            const watchedAuditors = watch(`audits.${index}.auditors`) || [];
            const filteredAuditors = getFilteredAuditors(index);
            
            return (
              <div key={field.id} className="bg-slate-50/50 rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-white">
                  <h4 className="text-sm font-semibold text-slate-800">Audit {index + 1}</h4>
                  {auditFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        removeAudit(index);
                        const newSearchTerms = { ...auditorSearchTerms };
                        delete newSearchTerms[index];
                        setAuditorSearchTerms(newSearchTerms);
                      }}
                      className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Remove audit"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Audit Type */}
                    <div>
                      <Label htmlFor={`auditTypeId-${index}`}>Audit Type *</Label>
                      <Controller
                        name={`audits.${index}.auditTypeId`}
                        control={auditControl}
                        render={({ field: { onChange, value } }) => {
                          const selectedOption = auditTypeOptions.find(opt => opt.value === value);
                          const options = getAvailableAuditTypes();
                          return (
                            <Select
                              options={options}
                              placeholder={loadingAuditTypes ? 'Loading audit types...' : 'Select audit type'}
                              value={selectedOption || null}
                              onChange={(opt: any) => {
                                onChange(opt?.value || '');
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            />
                          );
                        }}
                      />
                      {auditErrors.audits?.[index]?.auditTypeId && (
                        <p className="mt-1 text-sm text-red-600">{auditErrors.audits[index]?.auditTypeId?.message}</p>
                      )}
                    </div>

                    {/* Audit Frequency */}
                    <div>
                      <Label htmlFor={`auditFrequency-${index}`}>Frequency *</Label>
                      <Controller
                        name={`audits.${index}.auditFrequency`}
                        control={auditControl}
                        render={({ field: { onChange, value } }) => {
                          const selectedOption = auditFrequencyOptions.find(opt => opt.value === value);
                          return (
                            <Select
                              options={auditFrequencyOptions}
                              placeholder="Select frequency"
                              value={selectedOption || null}
                              onChange={(opt: any) => {
                                onChange(opt?.value || '');
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            />
                          );
                        }}
                      />
                      {auditErrors.audits?.[index]?.auditFrequency && (
                        <p className="mt-1 text-sm text-red-600">{auditErrors.audits[index]?.auditFrequency?.message}</p>
                      )}
                    </div>

                    {/* Audit Start Date */}
                    <div>
                      <Label htmlFor={`auditStartDate-${index}`}>Audit Start Date</Label>
                      <Controller
                        name={`audits.${index}.auditStartDate`}
                        control={auditControl}
                        render={({ field: { onChange, value, onBlur } }) => (
                          <input
                            id={`auditStartDate-${index}`}
                            type="date"
                            value={value || defaultAuditStart || ''}
                            onChange={(e) => onChange(e.target.value || undefined)}
                            onBlur={onBlur}
                            min={projectStartDate ? toInputDate(projectStartDate) : undefined}
                            max={projectEndDate ? toInputDate(projectEndDate) : undefined}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        )}
                      />
                      {auditErrors.audits?.[index]?.auditStartDate && (
                        <p className="mt-1 text-sm text-red-600">{auditErrors.audits[index]?.auditStartDate?.message}</p>
                      )}
                      {projectStartDate && (
                        <p className="mt-1 text-xs text-gray-500">Default: project start. Must be between project start and end.</p>
                      )}
                    </div>

                    {/* Scope */}
                    <div>
                      <Label htmlFor={`scope-${index}`}>Scope *</Label>
                      <Controller
                        name={`audits.${index}.scope`}
                        control={auditControl}
                        render={({ field: { onChange, value, onBlur } }) => (
                          <Input
                            id={`scope-${index}`}
                            type="number"
                            placeholder="Enter scope (e.g. 100)"
                            value={value || ''}
                            onChange={(e) => {
                              const numValue = e.target.value === '' ? undefined : Number(e.target.value);
                              onChange(numValue);
                            }}
                            onBlur={onBlur}
                            min={0}
                            max={100}
                            error={!!auditErrors.audits?.[index]?.scope}
                            hint={auditErrors.audits?.[index]?.scope?.message}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Auditors Selection */}
                  <div>
                    <Label className="mb-3">Auditors *</Label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="relative mb-3">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search auditors..."
                          value={auditorSearchTerms[index] || ''}
                          onChange={(e) => {
                            setAuditorSearchTerms({
                              ...auditorSearchTerms,
                              [index]: e.target.value,
                            });
                          }}
                          className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                        {auditorSearchTerms[index] && (
                          <button
                            type="button"
                            onClick={() => {
                              setAuditorSearchTerms({
                                ...auditorSearchTerms,
                                [index]: '',
                              });
                            }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                        {loadingUsers ? (
                          <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
                        ) : filteredAuditors.length === 0 ? (
                          <div className="text-center py-6 text-gray-500 text-sm">
                            {auditorSearchTerms[index] ? `No matches for "${auditorSearchTerms[index]}"` : 'No auditors available'}
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {filteredAuditors.map((auditor) => (
                              <Controller
                                key={auditor._id || auditor.id}
                                name={`audits.${index}.auditors`}
                                control={auditControl}
                                render={({ field: { onChange, value } }) => {
                                  const currentValues = value || [];
                                  const auditorId = (auditor._id || auditor.id) as string;
                                  const isChecked = currentValues.includes(auditorId);
                                  
                                  return (
                                    <label className="flex items-center gap-3 p-3 hover:bg-emerald-50 cursor-pointer transition-colors">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const newValues = e.target.checked
                                            ? [...currentValues, auditorId]
                                            : currentValues.filter((id: string) => id !== auditorId);
                                          onChange(newValues);
                                        }}
                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 text-sm truncate">{auditor.name}</p>
                                        <p className="text-xs text-gray-500 truncate">{auditor.email}</p>
                                      </div>
                                    </label>
                                  );
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      {watchedAuditors.length > 0 && (
                        <p className="mt-3 text-sm text-green-600 flex items-center gap-1">
                          {watchedAuditors.length} auditor{watchedAuditors.length > 1 ? 's' : ''} selected
                        </p>
                      )}
                      {auditErrors.audits?.[index]?.auditors && (
                        <p className="mt-2 text-sm text-red-600">{auditErrors.audits[index]?.auditors?.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => appendAudit({
                auditTypeId: undefined as any,
                auditFrequency: undefined as any,
                auditors: [],
                scope: projectScope ?? undefined,
                auditStartDate: defaultAuditStart || undefined,
              })}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add Another Audit
            </button>

            <div className="flex-1" />

            <button
              type="button"
              onClick={() => {
                resetAuditForm();
                setAuditorSearchTerms({});
              }}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={submittingAudits}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submittingAudits ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create Audits
                </>
              )}
            </button>
          </div>
        </form>
    </div>
  );
}
